import { FC, ReactElement, ReactNode } from 'react';
import i18next from 'i18next';
import { PromQuestionDto, PromQuestionLocalizedContentDto } from '../../../../shared/dtos/prom.dto';
import { getPromQuestionLocalizedContent } from '../../util/prom-utils';
import DataTable, { DataTableColumn } from '../data-table/DataTable';
import StatusBadge from '../data-table/StatusBadge';

type PromQuestionTableProps = {
  promQuestions: PromQuestionDto[];
  isLoading: boolean;
  toolbar?: ReactNode;
  onClick?: (promQuestion: PromQuestionDto) => void;
}

const PromQuestionTable: FC<PromQuestionTableProps> = ({
  promQuestions,
  isLoading,
  toolbar,
  onClick,
}: PromQuestionTableProps): ReactElement => {
  const getContent = (promQuestion: PromQuestionDto): PromQuestionLocalizedContentDto | null =>
    getPromQuestionLocalizedContent(promQuestion, i18next.language);

  const columns: DataTableColumn<PromQuestionDto>[] = [
    {
      key: 'title',
      header: i18next.t('promQuestion.title'),
      cell: (promQuestion: PromQuestionDto) => (
        <span className="fw-semibold">{getContent(promQuestion)?.title ?? '-'}</span>
      ),
    },
    {
      key: 'questionText',
      header: i18next.t('promQuestion.questionText'),
      width: '30%',
      cell: (promQuestion: PromQuestionDto) => getContent(promQuestion)?.questionText ?? '-',
    },
    {
      key: 'questionType',
      header: i18next.t('promQuestion.questionType'),
      cell: (promQuestion: PromQuestionDto) => i18next.t(`promQuestion.questionTypes.${promQuestion.questionType}`),
    },
    {
      key: 'trigger',
      header: i18next.t('promQuestion.trigger'),
      cell: (promQuestion: PromQuestionDto) => i18next.t(`promQuestionnaireDetails.trigger.${promQuestion.trigger}`),
    },
    {
      key: 'metric',
      header: i18next.t('promQuestion.metric'),
      cell: (promQuestion: PromQuestionDto) =>
        promQuestion.metric ? i18next.t(`enduranceTrainingType.promMetric.${promQuestion.metric}`) : '-',
    },
    {
      key: 'isActive',
      header: i18next.t('promQuestion.isActive'),
      cell: (promQuestion: PromQuestionDto) => (
        <StatusBadge
          isActive={promQuestion.isActive}
          label={i18next.t(promQuestion.isActive ? 'promQuestion.active' : 'promQuestion.inactive')}
        />
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={promQuestions}
      rowKey={(promQuestion: PromQuestionDto) => promQuestion.id}
      onRowClick={onClick}
      isLoading={isLoading}
      toolbar={toolbar}
    />
  );
};

export default PromQuestionTable;
